/**
 * 多版本菜单 N35-B2（owner Q16 / Q17 / Q19，plan §3.1 末条、§3.3、§3.11 第 1 条）：这一次光顾是扫哪张码进来的。
 *
 * ## 由地址参数带着走，零存储（review 第 4 轮改方向）
 * 进店身份写在地址上：`mmEntry=table|gtable|counter`（单桌码 / 通用桌码 / Counter 码）；**不带 = 没有 service**。
 * 像菜单预览码、点单链接码一样只从当前地址读，不写 vuex、sessionStorage、localStorage、cookie。
 * 前三轮用存储（vuex → 本标签页 sessionStorage + 导航类型）去猜「这一次光顾」，刷新 / 书签 / 后退 / bfcache / 桌面 pc.html 外壳 /
 * 3DS 整页跳转各有一种猜错的形状；地址参数让每一页的身份就是它自己的地址，这些形状都不用猜了。
 *
 * - **进店那一刻**（整页加载的第一个导航落在入口地址：单桌码 `/v/<店>/t/<桌>`、通用桌码 `/t/n-t`、Counter 码 `/c?portal=true`、
 *   外带码 `/v/o/<店>`、`/p`、`/t` …；入口判定沿用第 2 轮口径：重定向来源 / 打开的地址不是首页自己的地址）按地址算出身份，
 *   路由守卫（`menuAddress.js`，排在所有守卫之前）用一次 replace 写进地址（`landingEntryLocation`）。不带 service 的入口不写。
 * - **站内跳转**：同一个守卫把它从上一页补到下一页（`carryEntryValue`）；只在同一家店内补（换店、合单 `/vgs/`、集团码 `/vg/`、没有门店的页不补）。
 * - **Custom Flows**（`/v/sp/` 或路由 meta，Q19）：永远没有这个参数 —— 落地时去掉，站内不补，读的时候也不认。
 * - **读**（`menuEntry`：首页读菜单、九个下单点、重新解析）只看当前路由上的参数。刷新、书签、后退前进、bfcache 还原、
 *   桌面外壳的 iframe（`pc.html` 拿的就是补过参数的地址）都只是「打开某个地址」，身份照那个地址；
 *   地址上没有参数的非入口页（后端 / 银行拼的回跳地址、首页自己的地址被手输 / 书签）⇒ 不带（fail closed：只会少看到 Counter / 桌号菜单，
 *   下单回落 +「菜单刚更新」）。App 自己的整页跳转（card-detail 3DS 那几处）用 `withMenuAddress` 顺手带上。
 *
 * Q17：通用桌码进店（`gtable`）的顾客自己填的桌号**不进菜单 context**（只带 service:'table'），「只限某几张桌」的菜单对他们不符合；
 * 订单 details 里的桌号照旧。参数不是秘密（顾客能自己改地址；context 本来就是自报的，plan §6 第 13 条），只决定能看到哪些菜单。
 */
import { NO_INPUT_TABLE_NUMBER } from '@/utils/constants'
import { isSpRoute, isStoreVenue, qrEntryKind } from './qrMenu'
import { MENU_LINK_QUERY_KEY, currentMenuLinkCode, firstMenuLinkCode } from './menuLink'

/** 地址里的键（新站契约同名，plan §3.11） */
export const ENTRY_QUERY_KEY = 'mmEntry'

/** 取值：单桌码 / 通用桌码（Q17：菜单 context 不带自填桌号）/ Counter 码 */
export const ENTRY_VALUES = ['table', 'gtable', 'counter']

/** 点单首页（Custom Flows 首页另算：永远不写） */
const HOME_ROUTE_NAMES = ['venue-home-pick', 'venue-home-table', 'venue-home-no-table']

/** 首页自己的地址（站内回首页 / 刷新 / 书签打开的是它；不是扫的码）：`[/online|/r]/v[/o]/<店>/home/(p|t/<桌>)` */
const OWN_HOME_PATH = /^(\/online|\/r)?\/v(\/o)?\/[^/]+\/home\/(p|t\/[^/]+)\/?$/

/** 合单 / 集团码的地址（不补进店参数） */
const GROUP_PATH = /^(\/online)?\/vgs?(\/o)?\//

/** 一个值是不是合法的进店参数：是 → 原样返回；别的（大写、空串、数组、非字符串）→ null */
export function normalizeEntryValue(v) {
  return typeof v === 'string' && ENTRY_VALUES.includes(v) ? v : null
}

/** 一个 query 值（重复参数时 vue-router 给数组）里第一个合法的，没有 → null */
export function firstEntryValue(v) {
  const values = Array.isArray(v) ? v : [v]
  for (let i = 0; i < values.length; i++) {
    const value = normalizeEntryValue(values[i])
    if (value !== null) return value
  }
  return null
}

function queryOf(route) {
  return (route && route.query) || {}
}

function hasKey(query, key) {
  return Object.prototype.hasOwnProperty.call(query, key)
}

/** 一个入口地址是哪种码（plan §3.1 总表；只看地址，不看这一单怎么吃）：'table' / 'gtable' / 'counter' / null */
export function entryValueOf(route) {
  const r = route || {}
  const service = qrEntryKind({ routeName: r.name, path: r.path, query: r.query, isSpMode: isSpRoute(r) })
  if (service === 'counter') return 'counter'
  if (service !== 'table') return null
  const number = r.params && r.params.number
  return typeof number !== 'string' || !number.trim() || number === NO_INPUT_TABLE_NUMBER ? 'gtable' : 'table'
}

/**
 * 整页加载落地的这一页是不是「进店」：落在点单首页，而且打开的地址（重定向来源 `redirectedFrom`，没有就是落地的 path）不是首页自己的地址。
 * 具名重定向（`/c`、`/p`、`/t/<桌>`）有 redirectedFrom；别名（`/v/o/<店>`、`/online/v/<店>/…`）与直接路由（`/v/<店>/t`）没有，path 就是打开的那个
 * （真路由器实测，见 tests/unit/router/qrEntryLanding.spec.js）。Custom Flows 不算（永远不写，另有去掉的规则）。
 */
export function isEntryLanding(route) {
  if (!route || !HOME_ROUTE_NAMES.includes(route.name) || isSpRoute(route)) return false
  const opened = typeof route.redirectedFrom === 'string' && route.redirectedFrom ? route.redirectedFrom : route.path
  if (typeof opened !== 'string' || !opened) return false
  return !OWN_HOME_PATH.test(opened.split(/[?#]/)[0])
}

/** 同一个路由，换一份 query（具名重定向来的按路由名走：ssp 店的首页地址按地址走会撞上特殊静态重定向、落到别的页） */
function sameRoute(route, query, replace) {
  const base = route.redirectedFrom && typeof route.name === 'string'
    ? { name: route.name, params: { ...(route.params || {}) } }
    : { path: route.path }
  const location = { ...base, query, hash: route.hash || '' }
  if (replace) location.replace = true
  return location
}

/**
 * 整页加载的第一个导航落地时（路由守卫，这一页的第一个导航还没落地 —— router/index.js `firstNavigationLanded`）：
 * - Custom Flows 地址上有进店参数（任何值）⇒ 去掉（Q19）；
 * - 地址上已经有合法的进店参数（刷新、书签、后退前进、pc.html 的 iframe、带参数的整页跳转）⇒ 不动 —— 这一页的身份就是它；
 * - 落在入口地址、认得出 table / gtable / counter ⇒ 写进地址；
 * - 别的 ⇒ null（不动）。
 * 写 / 去掉都返回 `replace: true` 的地址（`next(location)`）：替换当前这一条历史，后退不会退到「没有参数」的那一条。
 * @returns {Object|null}
 */
export function landingEntryLocation(to) {
  if (!to || typeof to !== 'object') return null
  const query = queryOf(to)
  if (isSpRoute(to)) {
    if (!hasKey(query, ENTRY_QUERY_KEY)) return null
    const stripped = { ...query }
    delete stripped[ENTRY_QUERY_KEY]
    return sameRoute(to, stripped, true)
  }
  if (normalizeEntryValue(query[ENTRY_QUERY_KEY]) !== null) return null
  if (!isEntryLanding(to)) return null
  const value = entryValueOf(to)
  if (!value) return null
  return sameRoute(to, { ...query, [ENTRY_QUERY_KEY]: value }, true)
}

/** 两个门店键是不是同一家店（同一个键不分大小写；url_id 与 UUID 按 store 互认，`isStoreVenue`） */
function sameStore(a, b, state) {
  if (typeof a !== 'string' || !a || typeof b !== 'string' || !b) return false
  if (a.toLowerCase() === b.toLowerCase()) return true
  return isStoreVenue(state, a) && isStoreVenue(state, b)
}

/**
 * 站内跳转（路由守卫）：上一页地址上有合法的进店参数、下一页没有单个合法的 ⇒ 返回要补的值，否则 null。
 * 只在同一家店内补：下一页是 Custom Flows（Q19）、合单 / 集团码、没有门店、别家店 ⇒ 不补。
 */
export function carryEntryValue(to, from, state) {
  const value = firstEntryValue(queryOf(from)[ENTRY_QUERY_KEY])
  if (value === null || !to || typeof to !== 'object') return null
  if (normalizeEntryValue(queryOf(to)[ENTRY_QUERY_KEY]) !== null) return null
  if (isSpRoute(to) || GROUP_PATH.test(typeof to.path === 'string' ? to.path : '')) return null
  const toVenue = to.params && to.params.venueId
  const fromVenue = from.params && from.params.venueId
  return sameStore(fromVenue, toVenue, state) ? value : null
}

/**
 * 整页加载落地的这一页是不是**单店入口**（路由守卫用，review 第 4 轮 R4B5-2）：是 ⇒ 守卫清掉上一次走合单（`/vgs/`）留在持久化 vuex 里的
 * `venueHome.subGroupId` —— 不清的话 `menuEntry` 一律回 `{}`，这次从不带 `qr=1` 的 Counter 复制链接、带点单链接码的链接、被去掉 `qr` 的
 * iOS 桌面书签进来，读菜单 / 下单都不带 service、不带码（页面自己只在 `qr=1` 或预览时清，venue-home `_setupSubGroup`）。
 * 是：地址上有合法的进店参数或点单链接码，或者落地本身是入口（`isEntryLanding`：外带别名、`/p`、`/t` …）。
 * 不是：合单 / 集团码地址；地址带 `subGroupId`（合单页选进成员店就这么跳，页面自己会存它）；没有门店。
 * 只在整页加载时问（站内从合单页走到成员店、在成员店里去 `/v/<店>/t` 都不清）。合单流程里的页面地址上没有进店参数（守卫不往 `/vgs/` 补、
 * 也不从 `/vgs/` 补出来），所以「地址上有进店参数」只会是单店入口这一次光顾。
 */
export function isSingleStoreLanding(route) {
  if (!route || typeof route !== 'object') return false
  const path = typeof route.path === 'string' ? route.path : ''
  if (!path || GROUP_PATH.test(path)) return false
  if (!route.params || typeof route.params.venueId !== 'string' || !route.params.venueId) return false
  const query = queryOf(route)
  if (hasKey(query, 'subGroupId')) return false
  return firstEntryValue(query[ENTRY_QUERY_KEY]) !== null ||
    firstMenuLinkCode(query[MENU_LINK_QUERY_KEY]) !== null ||
    isEntryLanding(route)
}

/** 地址参数 → `{ service, generalTable }`；没有 / 认不出 ⇒ `{ service: null }` */
export function entryOfQuery(query) {
  const value = firstEntryValue((query || {})[ENTRY_QUERY_KEY])
  if (value === 'counter') return { service: 'counter', generalTable: false }
  if (value === 'table' || value === 'gtable') return { service: 'table', generalTable: value === 'gtable' }
  return { service: null }
}

/**
 * 读菜单 / 下单 / 重新解析时这一次光顾的码 —— venue-home `_getMenu`、九个下单点（`withOrderMenu` 第三参）、`reresolveMenu` 都传
 * `menuEntry(this.$store.state, this.$route)`。只读当前路由（不读 vuex、不读任何存储）：
 * - 合单 ⇒ `{}`（合单不解析菜单，D98①；合单两处下单也不经 withOrderMenu；点单链接码也不带）；
 * - Custom Flows 页面 ⇒ 不带 service（Q19），地址里有点单链接码照带（N35-B5）；
 * - 别的 ⇒ 地址上的进店参数（没有 ⇒ 不带，fail closed）+ 地址里的点单链接码（`menuLink.js`，现读、不存）。
 * store 里的 menuId 若是别的标签页解析的：这一单「那份菜单 + 本页地址的码」后端 300 秒复核不认 ⇒ 回落 +「菜单刚更新」再按这一单的 context 解析（menuCheck.js），安全方向。
 * @param {Object} state root state
 * @param {Object} [route] 当前路由（`this.$route`）
 * @returns {{service?: ('table'|'counter'|null), generalTable?: Boolean, menuLink?: String}}
 */
export function menuEntry(state, route) {
  if (!state) return {}
  if (state.venueHome && state.venueHome.subGroupId) return {}
  const query = queryOf(route)
  const entry = isSpRoute(route) ? { service: null } : entryOfQuery(query)
  const menuLink = currentMenuLinkCode(query)
  return menuLink ? { ...entry, menuLink } : entry
}
